import React, { useCallback, useEffect, useState } from 'react';

import { useCITranslate } from '../../i18n/customerIdentity';
import {
  deleteCustomerLogo,
  getCustomerHistory,
  uploadCustomerLogo,
} from '../../services/customerIdentityApi';
import { resolveCiAssetUrl } from '../../utils/ciAssets';
import CIEditHistory from './CIEditHistory';
import { CIInlineAlert } from './CustomerIdentityShared';

const EMPTY = {
  site_id: '',
  name: '',
  account_type: 'individual',
  email: '',
  phone: '',
  is_2fa_required: false,
  erp_customer_id: '',
  external_customer_id: '',
  status: 'active',
};

/**
 * Drawer/page form for creating or editing a customer.
 *
 * Props:
 *   customer       — existing customer when editing (omit to create).
 *   sites          — list of available sites (only required when creating).
 *   defaultSiteId  — preselected site when creating from a filtered list.
 *   onSubmit       — async handler; receives the form payload.
 *   onCancel       — close handler.
 *   busy           — disables the submit button while a save is in flight.
 *   formMessage    — optional `{ kind, error | i18nKey | text }` to display
 *                    inline above the fields (e.g. validation/save errors).
 *   onDismissMessage — clears `formMessage`.
 *   onLogoUpdated    — optional; receives updated customer after logo upload/remove.
 */
export default function CustomerForm({
  customer,
  sites,
  onSubmit,
  onCancel,
  busy,
  defaultSiteId,
  formMessage,
  onDismissMessage,
  onLogoUpdated,
  children,
}) {
  const t = useCITranslate();
  const [form, setForm] = useState(EMPTY);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState('');

  const fetchHistory = useCallback(
    () => (customer?.id ? getCustomerHistory(customer.id) : Promise.resolve([])),
    [customer?.id],
  );

  useEffect(() => {
    setForm({
      ...EMPTY,
      site_id: defaultSiteId || '',
      ...(customer || {}),
    });
  }, [customer, defaultSiteId]);

  useEffect(() => {
    setLogoError('');
  }, [customer?.id, customer?.metadata]);

  const logoPath =
    customer?.metadata && typeof customer.metadata === 'object'
      ? customer.metadata.customer_logo_url || customer.metadata.logo_url || ''
      : '';

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !customer?.id) return;
    setLogoError('');
    setLogoBusy(true);
    try {
      const updated = await uploadCustomerLogo(customer.id, file);
      onLogoUpdated?.(updated);
    } catch (err) {
      setLogoError(err.message || t('customer.logoUploadFailed'));
    } finally {
      setLogoBusy(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!customer?.id) return;
    setLogoError('');
    setLogoBusy(true);
    try {
      const updated = await deleteCustomerLogo(customer.id);
      onLogoUpdated?.(updated);
    } catch (err) {
      setLogoError(err.message || t('customer.logoRemoveFailed'));
    } finally {
      setLogoBusy(false);
    }
  };

  const setField = (key) => (e) => {
    setForm((prev) => ({
      ...prev,
      [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));
    if (formMessage?.kind === 'danger' && onDismissMessage) onDismissMessage();
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      email: form.email || null,
      phone: form.phone || null,
      erp_customer_id: form.erp_customer_id || null,
      external_customer_id: form.external_customer_id || null,
    };
    onSubmit?.(payload);
  };

  return (
    <form onSubmit={submit} noValidate>
      <CIInlineAlert message={formMessage} onDismiss={onDismissMessage} />

      {customer?.id ? (
        <div className="mb-3 pb-3 border-bottom">
          <label className="form-label">{t('customer.logo')}</label>
          <p className="small text-muted mb-2">{t('customer.logoHelp')}</p>
          {logoError ? (
            <div className="alert alert-danger py-2 small mb-2" role="alert">
              {logoError}
            </div>
          ) : null}
          <div className="d-flex flex-column flex-md-row align-items-start gap-3">
            {logoPath ? (
              <img
                src={resolveCiAssetUrl(logoPath)}
                alt=""
                className="border rounded bg-white p-1 flex-shrink-0"
                style={{ maxHeight: 72, maxWidth: 220, objectFit: 'contain' }}
              />
            ) : (
              <span className="text-muted small">{t('customer.logoNone')}</span>
            )}
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <label className="btn btn-outline-secondary btn-sm mb-0">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,.svg"
                  className="d-none"
                  disabled={busy || logoBusy}
                  onChange={handleLogoFile}
                />
                {logoBusy ? t('customer.uploadingLogo') : t('customer.uploadLogo')}
              </label>
              {logoPath ? (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  disabled={busy || logoBusy}
                  onClick={handleRemoveLogo}
                >
                  {t('customer.removeLogo')}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className="row g-3">
        {!customer ? (
          <div className="col-12 col-md-6">
            <label className="form-label">{t('customer.site')}</label>
            <select
              className="form-select"
              value={form.site_id}
              onChange={setField('site_id')}
              required
            >
              <option value="">{t('customer.selectSite')}</option>
              {(sites || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="col-12 col-md-6">
          <label className="form-label">{t('customer.accountType')}</label>
          <select
            className="form-select"
            value={form.account_type}
            onChange={setField('account_type')}
          >
            <option value="individual">
              {t('customer.accountTypes.individual')}
            </option>
            <option value="business">
              {t('customer.accountTypes.business')}
            </option>
          </select>
        </div>

        <div className="col-12 col-md-6">
          <label className="form-label">{t('customer.nameOrCompany')}</label>
          <input
            type="text"
            className="form-control"
            value={form.name}
            onChange={setField('name')}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label">{t('common.status')}</label>
          <select
            className="form-select"
            value={form.status || 'active'}
            onChange={setField('status')}
          >
            <option value="active">{t('customer.statuses.active')}</option>
            <option value="suspended">{t('customer.statuses.suspended')}</option>
            <option value="closed">{t('customer.statuses.closed')}</option>
          </select>
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label">{t('customer.email')}</label>
          <input
            type="email"
            className="form-control"
            value={form.email || ''}
            onChange={setField('email')}
          />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label">{t('customer.phone')}</label>
          <input
            type="tel"
            className="form-control"
            value={form.phone || ''}
            onChange={setField('phone')}
          />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label">{t('customer.erpCustomerId')}</label>
          <input
            type="text"
            className="form-control"
            value={form.erp_customer_id || ''}
            onChange={setField('erp_customer_id')}
          />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label">{t('customer.externalCustomerId')}</label>
          {customer ? (
            <p className="form-control-plaintext text-muted small mb-0">
              {form.external_customer_id || '—'}
            </p>
          ) : (
            <input
              type="text"
              className="form-control"
              value={form.external_customer_id || ''}
              onChange={setField('external_customer_id')}
            />
          )}
        </div>
        <div className="col-12">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="customer_2fa_required"
              checked={!!form.is_2fa_required}
              onChange={setField('is_2fa_required')}
            />
            <label className="form-check-label" htmlFor="customer_2fa_required">
              {t('customer.require2fa')}
            </label>
          </div>
        </div>
      </div>

      {children}

      {customer?.id ? (
        <CIEditHistory
          fetchHistory={fetchHistory}
          updatedAt={customer.updated_at}
          resourceId={customer.id}
        />
      ) : null}

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? t('common.saving') : t('customer.saveCustomer')}
        </button>
      </div>
    </form>
  );
}
